import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import AssessmentNavigation from '../../components/ui/AssessmentNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EquipmentInventoryTable from './components/EquipmentInventoryTable';
import CapacityAnalysisChart from './components/CapacityAnalysisChart';
import ScaleUpCalculator from './components/ScaleUpCalculator';
import EquipmentLifecycleChart from './components/EquipmentLifecycleChart';
import MaintenanceScheduleCard from './components/MaintenanceScheduleCard';
import RiskIndicatorPanel from './components/RiskIndicatorPanel';
import AddEquipmentModal from './components/AddEquipmentModal';

const EquipmentAnalysis = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inventory');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [equipmentData, setEquipmentData] = useState([
    {
      id: 1,
      name: "Mesin Pasteurisasi Utama",
      brand: "Tetra Pak",
      capacity: 500,
      unit: "Liter/Jam",
      age: 5,
      condition: "Baik",
      utilization: 85,
      lastMaintenance: "15 Januari 2026",
      icon: "Flame"
    },
    {
      id: 2,
      name: "Tangki Penyimpanan Dingin",
      brand: "Mueller",
      capacity: 2000,
      unit: "Liter",
      age: 3,
      condition: "Sangat Baik",
      utilization: 75,
      lastMaintenance: "28 Januari 2026",
      icon: "Snowflake"
    },
    {
      id: 3,
      name: "Mesin Pengemas Otomatis",
      brand: "Bosch",
      capacity: 300,
      unit: "Unit/Jam",
      age: 7,
      condition: "Cukup",
      utilization: 90,
      lastMaintenance: "10 Desember 2025",
      icon: "Package"
    },
    {
      id: 4,
      name: "Sistem Filtrasi",
      brand: "GEA",
      capacity: 400,
      unit: "Liter/Jam",
      age: 4,
      condition: "Baik",
      utilization: 70,
      lastMaintenance: "20 Januari 2026",
      icon: "Droplet"
    },
    {
      id: 5,
      name: "Homogenizer",
      brand: "APV",
      capacity: 350,
      unit: "Liter/Jam",
      age: 6,
      condition: "Cukup",
      utilization: 80,
      lastMaintenance: "05 Januari 2026",
      icon: "Zap"
    }
  ]);

  const capacityAnalysisData = [
    {
      equipment: "Pasteurisasi",
      current: 500,
      required: 800,
      gap: 300,
      unit: "L/Jam"
    },
    {
      equipment: "Penyimpanan",
      current: 2000,
      required: 3500,
      gap: 1500,
      unit: "Liter"
    },
    {
      equipment: "Pengemasan",
      current: 300,
      required: 500,
      gap: 200,
      unit: "Unit/Jam"
    },
    {
      equipment: "Filtrasi",
      current: 400,
      required: 700,
      gap: 300,
      unit: "L/Jam"
    },
    {
      equipment: "Homogenisasi",
      current: 350,
      required: 600,
      gap: 250,
      unit: "L/Jam"
    }
  ];

  const lifecycleData = [
    { year: 0, performance: 100, maintenanceCost: 5 },
    { year: 1, performance: 98, maintenanceCost: 8 },
    { year: 2, performance: 95, maintenanceCost: 12 },
    { year: 3, performance: 92, maintenanceCost: 18 },
    { year: 4, performance: 88, maintenanceCost: 25 },
    { year: 5, performance: 82, maintenanceCost: 35 },
    { year: 6, performance: 75, maintenanceCost: 48 },
    { year: 7, performance: 65, maintenanceCost: 65 },
    { year: 8, performance: 55, maintenanceCost: 85 },
    { year: 9, performance: 42, maintenanceCost: 100 },
    { year: 10, performance: 30, maintenanceCost: 120 }
  ];

  const maintenanceSchedules = [
    {
      id: 1,
      equipment: "Mesin Pasteurisasi Utama",
      maintenanceType: "Pemeliharaan Rutin",
      scheduledDate: "15 Februari 2026",
      technician: "Ahmad Fauzi",
      estimatedCost: 5000000,
      status: "Terjadwal",
      icon: "Flame",
      notes: "Penggantian seal dan pembersihan heat exchanger"
    },
    {
      id: 2,
      equipment: "Mesin Pengemas Otomatis",
      maintenanceType: "Perbaikan Mendesak",
      scheduledDate: "05 Februari 2026",
      technician: "Budi Santoso",
      estimatedCost: 8000000,
      status: "Mendesak",
      icon: "Package",
      notes: "Perbaikan sistem kontrol dan kalibrasi sensor"
    },
    {
      id: 3,
      equipment: "Tangki Penyimpanan Dingin",
      maintenanceType: "Inspeksi Berkala",
      scheduledDate: "28 Februari 2026",
      technician: "Siti Nurhaliza",
      estimatedCost: 3000000,
      status: "Terjadwal",
      icon: "Snowflake",
      notes: "Pemeriksaan sistem pendingin dan isolasi"
    },
    {
      id: 4,
      equipment: "Homogenizer",
      maintenanceType: "Overhaul",
      scheduledDate: "10 Maret 2026",
      technician: "Dedi Kurniawan",
      estimatedCost: 12000000,
      status: "Tertunda",
      icon: "Zap",
      notes: "Penggantian komponen utama dan kalibrasi ulang"
    }
  ];

  const riskIndicators = [
    {
      id: 1,
      category: "Kapasitas",
      level: "Tinggi",
      title: "Kesenjangan Kapasitas Pasteurisasi",
      description: "Kapasitas pasteurisasi saat ini tidak mencukupi untuk target produksi scale-up. Utilisasi sudah mencapai 85% dengan risiko bottleneck.",
      impacts: [
        "Keterlambatan produksi hingga 30%",
        "Penurunan kualitas produk akibat overload",
        "Peningkatan biaya operasional"
      ],
      recommendation: "Investasi mesin pasteurisasi tambahan dengan kapasitas 500 L/Jam dalam 3 bulan ke depan"
    },
    {
      id: 2,
      category: "Kondisi",
      level: "Sedang",
      title: "Degradasi Mesin Pengemas",
      description: "Mesin pengemas berusia 7 tahun dengan kondisi cukup dan utilisasi 90%. Risiko kerusakan mendadak tinggi.",
      impacts: [
        "Downtime produksi tidak terduga",
        "Biaya perbaikan darurat tinggi",
        "Kehilangan efisiensi pengemasan"
      ],
      recommendation: "Jadwalkan overhaul komprehensif atau pertimbangkan penggantian dalam 6 bulan"
    },
    {
      id: 3,
      category: "Pemeliharaan",
      level: "Sedang",
      title: "Jadwal Pemeliharaan Tertunda",
      description: "Homogenizer memiliki jadwal overhaul yang tertunda dengan risiko penurunan performa signifikan.",
      impacts: [
        "Penurunan kualitas homogenisasi",
        "Peningkatan konsumsi energi",
        "Risiko kerusakan komponen kritis"
      ],
      recommendation: "Prioritaskan overhaul homogenizer sebelum scale-up produksi"
    },
    {
      id: 4,
      category: "Investasi",
      level: "Tinggi",
      title: "Kebutuhan Investasi Peralatan Besar",
      description: "Estimasi investasi peralatan untuk scale-up mencapai Rp 325M dengan kebutuhan mendesak dalam 6 bulan.",
      impacts: [
        "Tekanan cashflow signifikan",
        "Keterlambatan implementasi scale-up",
        "Risiko kehilangan momentum pasar"
      ],
      recommendation: "Susun rencana investasi bertahap dengan prioritas peralatan kritis"
    },
    {
      id: 5,
      category: "Operasional",
      level: "Rendah",
      title: "Efisiensi Sistem Filtrasi",
      description: "Sistem filtrasi beroperasi pada utilisasi 70% dengan kondisi baik, memberikan buffer untuk scale-up.",
      impacts: [
        "Kapasitas cadangan tersedia",
        "Fleksibilitas operasional tinggi"
      ],
      recommendation: "Pertahankan jadwal pemeliharaan rutin untuk memaksimalkan umur peralatan"
    }
  ];

  const handleEditEquipment = (equipment) => {
    console.log('Edit equipment:', equipment);
  };

  const handleDeleteEquipment = (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus peralatan ini?')) {
      setEquipmentData(prev => prev?.filter(item => item?.id !== id));
    }
  };

  const handleSaveEquipment = (newEquipment) => {
    setEquipmentData(prev => [...prev, newEquipment]);
  };

  const handleCalculate = (results) => {
    console.log('Calculation results:', results);
  };

  const handleSaveAndContinue = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      navigate('/financial-analysis-form');
    }, 1500);
  };

  const handleSaveDraft = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      alert('Data berhasil disimpan sebagai draft');
    }, 1000);
  };

  const tabs = [
    { id: 'inventory', label: 'Inventaris Peralatan', icon: 'Package' },
    { id: 'capacity', label: 'Analisis Kapasitas', icon: 'BarChart3' },
    { id: 'calculator', label: 'Kalkulator Scale-Up', icon: 'Calculator' },
    { id: 'lifecycle', label: 'Siklus Hidup', icon: 'TrendingUp' },
    { id: 'maintenance', label: 'Jadwal Pemeliharaan', icon: 'Wrench' },
    { id: 'risks', label: 'Indikator Risiko', icon: 'AlertTriangle' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressIndicator currentStep={4} completedSteps={[1, 2, 3]} />
      <AssessmentNavigation completedModules={['supplier', 'operational']} />
      <main className="with-assessment-nav">
        <div className="container-executive section-spacing">
          {/* Page Header */}
          <div className="mb-8 md:mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Wrench" size={28} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                  Analisis Peralatan
                </h1>
                <p className="text-base md:text-lg text-muted-foreground mt-2">
                  Evaluasi komprehensif peralatan dan perhitungan kebutuhan scale-up
                </p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-card rounded-xl border border-border p-2 mb-8 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="hidden md:inline">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
            {activeTab === 'inventory' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                      Inventaris Peralatan
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Daftar lengkap peralatan produksi dengan status dan kondisi
                    </p>
                  </div>
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => setIsModalOpen(true)}
                  >
                    Tambah Peralatan
                  </Button>
                </div>

                <div className="bg-card rounded-xl border border-border p-6 md:p-8">
                  <EquipmentInventoryTable
                    equipment={equipmentData}
                    onEdit={handleEditEquipment}
                    onDelete={handleDeleteEquipment}
                  />
                </div>
              </div>
            )}

            {activeTab === 'capacity' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                    Analisis Kapasitas
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Perbandingan kapasitas saat ini dengan kebutuhan scale-up
                  </p>
                </div>

                <div className="bg-card rounded-xl border border-border p-6 md:p-8">
                  <CapacityAnalysisChart data={capacityAnalysisData} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-card rounded-xl border border-border p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
                      <p className="text-sm text-muted-foreground">Total Kesenjangan</p>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-foreground data-text">2.550</p>
                    <p className="text-xs text-muted-foreground mt-1">Unit Kapasitas</p>
                  </div>

                  <div className="bg-card rounded-xl border border-border p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon name="Percent" size={20} color="var(--color-warning)" />
                      <p className="text-sm text-muted-foreground">Rata-rata Utilisasi</p>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-foreground data-text">80%</p>
                    <p className="text-xs text-muted-foreground mt-1">Dari Kapasitas Maksimal</p>
                  </div>

                  <div className="bg-card rounded-xl border border-border p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
                      <p className="text-sm text-muted-foreground">Bottleneck Kritis</p>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-foreground data-text">2</p>
                    <p className="text-xs text-muted-foreground mt-1">Peralatan Perlu Upgrade</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'calculator' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                    Kalkulator Scale-Up
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Hitung kebutuhan peralatan berdasarkan target produksi
                  </p>
                </div>

                <ScaleUpCalculator onCalculate={handleCalculate} />
              </div>
            )}

            {activeTab === 'lifecycle' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                    Siklus Hidup Peralatan
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Proyeksi performa dan biaya pemeliharaan sepanjang umur peralatan
                  </p>
                </div>

                <div className="bg-card rounded-xl border border-border p-6 md:p-8">
                  <EquipmentLifecycleChart data={lifecycleData} />
                </div>

                <div className="bg-accent/10 border border-accent/20 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Lightbulb" size={20} color="var(--color-accent)" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground mb-2">
                        Insight Siklus Hidup
                      </h3>
                      <p className="text-sm text-foreground mb-3">
                        Grafik menunjukkan penurunan performa peralatan seiring waktu dengan peningkatan biaya pemeliharaan. 
                        Titik optimal penggantian peralatan adalah pada tahun ke-7 hingga ke-8 ketika biaya pemeliharaan 
                        melebihi 60% dari nilai performa.
                      </p>
                      <ul className="space-y-2 text-sm text-foreground">
                        <li className="flex items-start gap-2">
                          <Icon name="CheckCircle2" size={16} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                          <span>Tahun 0-3: Periode optimal dengan biaya pemeliharaan rendah</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" color="var(--color-warning)" />
                          <span>Tahun 4-7: Periode transisi dengan peningkatan biaya bertahap</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="XCircle" size={16} className="flex-shrink-0 mt-0.5" color="var(--color-error)" />
                          <span>Tahun 8+: Periode kritis dengan biaya tinggi dan performa rendah</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'maintenance' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                    Jadwal Pemeliharaan
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Rencana pemeliharaan preventif dan perbaikan peralatan
                  </p>
                </div>

                <MaintenanceScheduleCard schedules={maintenanceSchedules} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-card rounded-xl border border-border p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon name="Calendar" size={20} color="var(--color-primary)" />
                      <h3 className="text-base font-semibold text-foreground">Ringkasan Jadwal</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Total Jadwal</span>
                        <span className="text-sm font-medium text-foreground data-text">4 Kegiatan</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Mendesak</span>
                        <span className="text-sm font-medium text-error data-text">1 Kegiatan</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Tertunda</span>
                        <span className="text-sm font-medium text-warning data-text">1 Kegiatan</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-xl border border-border p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon name="DollarSign" size={20} color="var(--color-success)" />
                      <h3 className="text-base font-semibold text-foreground">Estimasi Biaya</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Total Biaya</span>
                        <span className="text-sm font-medium text-foreground data-text">Rp 28M</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Bulan Ini</span>
                        <span className="text-sm font-medium text-foreground data-text">Rp 16M</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Bulan Depan</span>
                        <span className="text-sm font-medium text-foreground data-text">Rp 12M</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'risks' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                    Indikator Risiko Peralatan
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Identifikasi risiko terkait peralatan dan rekomendasi mitigasi
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-error/10 border border-error/20 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
                      <p className="text-sm text-error font-medium">Risiko Tinggi</p>
                    </div>
                    <p className="text-3xl font-bold text-error data-text">2</p>
                    <p className="text-xs text-error/80 mt-1">Memerlukan Tindakan Segera</p>
                  </div>

                  <div className="bg-warning/10 border border-warning/20 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
                      <p className="text-sm text-warning font-medium">Risiko Sedang</p>
                    </div>
                    <p className="text-3xl font-bold text-warning data-text">2</p>
                    <p className="text-xs text-warning/80 mt-1">Perlu Monitoring Ketat</p>
                  </div>

                  <div className="bg-success/10 border border-success/20 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
                      <p className="text-sm text-success font-medium">Risiko Rendah</p>
                    </div>
                    <p className="text-3xl font-bold text-success data-text">1</p>
                    <p className="text-xs text-success/80 mt-1">Kondisi Terkendali</p>
                  </div>
                </div>

                <RiskIndicatorPanel risks={riskIndicators} />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-12 flex items-center gap-4 flex-wrap">
            <Button
              variant="outline"
              size="lg"
              iconName="Save"
              iconPosition="left"
              onClick={handleSaveDraft}
              loading={isSaving}
              className="flex-1 md:flex-initial"
            >
              Simpan Draft
            </Button>
            <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={handleSaveAndContinue}
              loading={isSaving}
              className="flex-1 md:flex-initial"
            >
              Simpan & Lanjutkan
            </Button>
          </div>
        </div>
      </main>
      <AddEquipmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveEquipment}
      />
    </div>
  );
};

export default EquipmentAnalysis;